/**
 * Marketing Settings `?section=` keys, kept apart from the section components (`settings-sections.ts`) so the
 * route's `validateSearch`/`beforeLoad` can read them without pulling every section's UI into the entry bundle.
 * Nav order; the first key is the default when no `?section=` is set.
 */
export const SETTINGS_SECTION_KEYS = [
  'legal-pages',
  'consent-copy',
  'messaging',
  'quiet-hours',
  'ad-tracking',
  'visibility',
] as const;

/** A registered section key — typed so every `search={{ section }}` deep link is checked at compile time. */
export type SettingsSectionKey = (typeof SETTINGS_SECTION_KEYS)[number];

const DEFAULT_SECTION: SettingsSectionKey = SETTINGS_SECTION_KEYS[0];

function isSectionKey(value: unknown): value is SettingsSectionKey {
  return typeof value === 'string' && (SETTINGS_SECTION_KEYS as readonly string[]).includes(value);
}

/** Coerce an unknown `?section=` value to a registered key, falling back to the default. */
export function resolveSectionKey(value: unknown): SettingsSectionKey {
  return isSectionKey(value) ? value : DEFAULT_SECTION;
}

/**
 * Sections that moved to Settings › Integrations (the only place outside accounts are connected). Old links
 * to them — bookmarks, Facebook sign-ins started before the move — are redirected there by the route.
 */
const SECTIONS_MOVED_TO_INTEGRATIONS: ReadonlySet<string> = new Set(['facebook-instagram']);

/** Whether a raw `?section=` value names a section that now lives in Settings › Integrations. */
export function isSectionMovedToIntegrations(value: unknown): boolean {
  return typeof value === 'string' && SECTIONS_MOVED_TO_INTEGRATIONS.has(value);
}
